import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0"
};

const IpdDoctorVisits = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [currentMode, setCurrentMode] = useState("add");
  
  // Search States
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [admissionResults, setAdmissionResults] = useState([]);
  const [showAdmissionDropdown, setShowAdmissionDropdown] = useState(false);

  const [doctorSearch, setDoctorSearch] = useState("");
  const [doctorResults, setDoctorResults] = useState([]);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [formData, setFormData] = useState({
    admission_id: "",
    patient_id: "",
    patient_name: "",
    department: "",
    consulting_doctor: "",
    ward_type: "",
    room_no: "",
    bed_no: "",
    age: "",
    gender: "",
    visits_array: []
  });

  const [currentVisit, setCurrentVisit] = useState({
    visit_time: new Date().toISOString().slice(0, 16),
    visit_type: "Morning Round",
    primary_consultant: "No",
    assessment_notes: "",
    investigation_advice: "",
    medication_changes: ""
  });

  // --- ADMISSION SEARCH ---
  const handleAdmissionSearch = async (val) => {
    setAdmissionSearch(val);
    if (val.length < 3) {
      setAdmissionResults([]);
      setShowAdmissionDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      // Reusing the same vitals admission search API since it fetches admitted patients perfectly
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/admission-vitals/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setAdmissionResults(data);
      setShowAdmissionDropdown(true);
    } catch (err) {
      console.error(err);
    }
  };

  const loadExistingVisits = async (admissionId) => {
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd-doctor-visits/${admissionId}`);
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, visits_array: data }));
      }
    } catch (err) {
      console.error("Failed to load existing visits");
    }
  };

  const selectAdmission = (adm) => {
    setFormData(prev => ({
      ...prev,
      admission_id: adm.admission_id,
      patient_id: adm.patient_id,
      patient_name: adm.patient_name,
      department: adm.department,
      consulting_doctor: adm.consulting_doctor || adm.doctor_name || "N/A", // Assuming consulting doctor is in the admission record
      ward_type: adm.ward_type,
      room_no: adm.room_no,
      bed_no: adm.bed_no,
      age: adm.age,
      gender: adm.gender
    }));
    setAdmissionSearch(adm.admission_id);
    setShowAdmissionDropdown(false);
    
    if (screenAction === "edit" || screenAction === "view") {
        loadExistingVisits(adm.admission_id);
    }
    
    if (screenAction === "edit" || screenAction === "add") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- DUTY DOCTOR SEARCH ---
  const handleDoctorSearch = async (val) => {
    setDoctorSearch(val);
    if (val.length < 3) {
      setDoctorResults([]);
      setShowDoctorDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/doctors/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setDoctorResults(data);
      setShowDoctorDropdown(true);
    } catch (err) {
      console.error(err);
    }
  };

  const selectDoctor = (doc) => {
    setSelectedDoctor(doc);
    setDoctorSearch(doc.doctor_name);
    setShowDoctorDropdown(false);
  };

  // --- VISIT ARRAY MANAGEMENT ---
  const addVisit = () => {
    if (!selectedDoctor) return alert("Please search and select the Duty Doctor making the visit.");
    if (!currentVisit.assessment_notes) return alert("Please enter Assessment/Progress Notes.");

    setFormData(prev => ({
      ...prev,
      visits_array: [{
          ...currentVisit,
          consulting_doc_cd: "", // Assuming the main consulting doctor is stored differently, or leave blank if duty doc is acting consultant
          consulting_doc_name: formData.consulting_doctor,
          department: formData.department,
          duty_doctor_cd: selectedDoctor.doc_cd,
          duty_doctor_name: selectedDoctor.doctor_name
      }, ...prev.visits_array]
    }));

    setCurrentVisit({
      visit_time: new Date().toISOString().slice(0, 16),
      visit_type: "Morning Round",
      primary_consultant: "No",
      assessment_notes: "",
      investigation_advice: "",
      medication_changes: ""
    });
  };

  const removeVisit = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      visits_array: prev.visits_array.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // --- ACTIONS ---
  const clearForm = () => {
    setFormData({ admission_id: "", patient_id: "", patient_name: "", department: "", consulting_doctor: "", ward_type: "", room_no: "", bed_no: "", age: "", gender: "", visits_array: [] });
    setAdmissionSearch("");
    setDoctorSearch("");
    setSelectedDoctor(null);
    setIsDisabled(screenAction === "view");
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") { 
      setCurrentMode("add"); 
      clearForm(); 
      setIsDisabled(false); 
    }
    if (screenAction === "edit") { 
      setCurrentMode("edit"); 
      setIsDisabled(false); 
    }
    if (screenAction === "view") { 
      setCurrentMode("view"); 
      setIsDisabled(true); 
    }
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleSubmit = async () => {
    if (!formData.admission_id) return alert("Please select an Admission.");
    if (formData.visits_array.length === 0) return alert("No visits to save.");

    const user = JSON.parse(localStorage.getItem("user"));
    const payload = {
      admission_id: formData.admission_id,
      created_by: user.id,
      visits: formData.visits_array
    };

    const method = currentMode === "edit" ? "PUT" : "POST";
    const url = currentMode === "edit"
      ? `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-doctor-visits/${formData.admission_id}`
      : `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-doctor-visits`;

    try {
      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert(currentMode === "edit" ? "Doctor Visits Updated Successfully!" : "Doctor Visits Logged Successfully!");
        if (currentMode === "add") clearForm();
      } else {
        alert("Error saving visits.");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  const printForm = () => {
    if (!formData.admission_id) return alert("Please select an admission to print.");
    const newWindow = window.open("", "", "width=1000,height=800");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Doctor Visits - ${formData.admission_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #1b325f; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px; }
            .data-item { font-size: 13px; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; color: #555; display: inline-block; width: 140px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 13px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
            th { background-color: #f4f0fa; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Inpatient Clinical Management</p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin:0; color:#282f81;">DOCTOR PROGRESS NOTES</h2>
              <p style="margin:2px 0; font-size:12px;">Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Patient & Admission Details</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Admission ID:</span> ${formData.admission_id}</div>
            <div class="data-item"><span class="label">Patient Name:</span> ${formData.patient_name}</div>
            <div class="data-item"><span class="label">Age / Gender:</span> ${formData.age} / ${formData.gender}</div>
            <div class="data-item"><span class="label">Consulting Doctor:</span> <span style="font-weight:bold">${formData.consulting_doctor}</span></div>
            <div class="data-item" style="grid-column: span 2"><span class="label">Location:</span> ${formData.department} | ${formData.ward_type} (Room: ${formData.room_no} / Bed: ${formData.bed_no})</div>
          </div>

          <div class="section-header">Visit Records</div>
          <table>
            <thead>
              <tr>
                <th style="width: 15%">Date & Time</th>
                <th style="width: 15%">Duty Doctor</th>
                <th style="width: 15%">Visit Type</th>
                <th style="width: 25%">Assessment Notes</th>
                <th style="width: 15%">Investigation Advice</th>
                <th style="width: 15%">Medication Changes</th>
              </tr>
            </thead>
            <tbody>
              ${formData.visits_array.map(v => `
                <tr>
                  <td>${new Date(v.visit_time).toLocaleString()}</td>
                  <td style="font-weight:bold;">${v.duty_doctor_name}</td>
                  <td>${v.visit_type} <br><span style="font-size:10px; color:#888;">(Primary: ${v.primary_consultant})</span></td>
                  <td>${v.assessment_notes || '-'}</td>
                  <td>${v.investigation_advice || '-'}</td>
                  <td>${v.medication_changes || '-'}</td>
                </tr>
              `).join('')}
              ${formData.visits_array.length === 0 ? '<tr><td colspan="6" style="text-align:center;">No doctor visits recorded yet.</td></tr>' : ''}
            </tbody>
          </table>

          <div class="footer">System Generated Report - Emedha HMS</div>
        </body>
      </html>
    `;
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      
      {/* TOP SEARCH BOX (Only visible in Edit or View Mode) */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Admission </label>
          <input
            type="text"
            value={admissionSearch}
            placeholder="Search by Admission ID, Patient ID, Name, or Mobile..."
            onChange={(e) => handleAdmissionSearch(e.target.value)}
            autoFocus
            autoComplete="off"
          />
          {showAdmissionDropdown && admissionResults.length > 0 && (
            <div className="patient-dropdown">
              {admissionResults.map((adm) => (
                <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                  <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id} | Ph: {adm.mobile}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* 1. PATIENT & ADMISSION DETAILS */}
        <div className="form-section-header"><h3>1. Patient & Admission Details</h3></div>
        <div 
          className="form-grid" 
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "15px", 
            alignItems: "flex-end" 
          }}
        >
          {screenAction === "add" ? (
            <div className="input-box" style={{ position: "relative" }}>
              <label>Search Admission <span style={{color:'red'}}>*</span></label>
              <input 
                type="text" 
                value={admissionSearch} 
                onChange={(e) => handleAdmissionSearch(e.target.value)} 
                placeholder="Start typing to search..." 
                autoComplete="off"
              />
              {showAdmissionDropdown && admissionResults.length > 0 && (
                <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                  {admissionResults.map((adm) => (
                    <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                      <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="input-box read-only">
              <label>Admission ID</label>
              <input type="text" value={formData.admission_id} readOnly placeholder="Select from search above" />
            </div>
          )}

          <div className="input-box read-only">
            <label>Patient Name</label>
            <input type="text" value={formData.patient_name} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Department</label>
            <input type="text" value={formData.department} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Consulting Doctor</label>
            <input type="text" value={formData.consulting_doctor} readOnly />
          </div>
        </div>

        {/* 2. STAFF / DUTY DOCTOR SELECTION */}
        <div className="form-section-header" style={{ marginTop: "20px" }}><h3>2. Duty Doctor Details</h3></div>
        <div className="form-grid">
          <div className="input-box" style={{ position: "relative", maxWidth: "400px", width: "100%" }}>
            <label>Search Duty Doctor (Name, ID, Dept) <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              value={doctorSearch} 
              onChange={(e) => handleDoctorSearch(e.target.value)} 
              placeholder="Search Doctor..." 
              disabled={isDisabled}
              autoComplete="off"
            />
            {showDoctorDropdown && doctorResults.length > 0 && (
              <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                {doctorResults.map((doc) => (
                  <div key={doc.doc_cd} className="patient-item" onClick={() => selectDoctor(doc)}>
                    {doc.doc_cd} | {doc.doctor_name} | {doc.department}
                  </div>
                ))}
              </div>
            )}
            {selectedDoctor && (
               <p style={{ margin: "5px 0 0 0", fontSize: "12px", color: theme.primaryBlue, fontWeight: "bold" }}>
                 ✓ Selected: {selectedDoctor.doctor_name} ({selectedDoctor.department})
               </p>
            )}
          </div>
        </div>

        {/* 3. ADD VISIT LOG */}
        <div className="form-section-header" style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>3. Log Visit Details</h3>
          <button 
            type="button" 
            onClick={addVisit} 
            disabled={isDisabled || !formData.admission_id}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: theme.primaryBlue, 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: isDisabled || !formData.admission_id ? 'not-allowed' : 'pointer', 
              fontWeight: 'bold',
              opacity: isDisabled || !formData.admission_id ? 0.6 : 1
            }}>
            + Add Visit
          </button>
        </div>

        <div 
          className="form-grid" 
          style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "15px", 
            borderRadius: "8px", 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "15px",
            alignItems: "flex-start" 
          }}
        >
          <div className="input-box">
            <label>Date & Time</label>
            <input 
              type="datetime-local" 
              value={currentVisit.visit_time} 
              onChange={(e) => setCurrentVisit({...currentVisit, visit_time: e.target.value})} 
              disabled={isDisabled} 
            />
          </div>

          <div className="input-box">
            <label>Visit Type</label>
            <select 
              value={currentVisit.visit_type} 
              disabled={isDisabled}
              onChange={(e) => setCurrentVisit({ ...currentVisit, visit_type: e.target.value })}
            >
              <option value="Morning Round">Morning Round</option>
              <option value="Evening Round">Evening Round</option>
              <option value="Emergency visit">Emergency visit</option>
              <option value="Follow-up">Follow-up</option>
            </select>
          </div>

          <div className="input-box">
            <label>Primary Consultant?</label>
            <select 
              value={currentVisit.primary_consultant} 
              disabled={isDisabled}
              onChange={(e) => setCurrentVisit({ ...currentVisit, primary_consultant: e.target.value })}
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="input-box">
            <label>Assessment / Progress Notes</label>
            <input 
              type="text" 
              value={currentVisit.assessment_notes} 
              onChange={(e) => setCurrentVisit({...currentVisit, assessment_notes: e.target.value})} 
              disabled={isDisabled} 
              placeholder="Enter clinical assessment..." 
            />
          </div>

          <div className="input-box">
            <label>Investigation Advice</label>
            <input 
              type="text" 
              value={currentVisit.investigation_advice} 
              onChange={(e) => setCurrentVisit({...currentVisit, investigation_advice: e.target.value})} 
              disabled={isDisabled} 
              placeholder="e.g. CBC, X-Ray..." 
            />
          </div>

          <div className="input-box">
            <label>Medication Changes</label>
            <input 
              type="text" 
              value={currentVisit.medication_changes} 
              onChange={(e) => setCurrentVisit({...currentVisit, medication_changes: e.target.value})} 
              disabled={isDisabled} 
              placeholder="Any changes to prescriptions?" 
            />
          </div>
        </div>

        {/* 4. VISITS LIST TABLE */}
        {formData.visits_array.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: theme.primaryDark }}>Recorded Visits ({formData.visits_array.length})</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px', minWidth: '900px' }}>
                <thead style={{ backgroundColor: theme.bgLight, borderBottom: '2px solid #ccc' }}>
                  <tr>
                    <th style={{ padding: '10px', width: '15%' }}>Time</th>
                    <th style={{ padding: '10px', width: '15%' }}>Duty Doctor</th>
                    <th style={{ padding: '10px', width: '15%' }}>Visit Type</th>
                    <th style={{ padding: '10px', width: '20%' }}>Assessment Notes</th>
                    <th style={{ padding: '10px', width: '15%' }}>Investigations</th>
                    <th style={{ padding: '10px', width: '15%' }}>Med Changes</th>
                    <th style={{ padding: '10px', textAlign: 'center', width: '5%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.visits_array.map((visit, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd', backgroundColor: visit.id ? '#ffffff' : '#f4fcff' }}>
                      
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="datetime-local" 
                          value={new Date(visit.visit_time).toISOString().slice(0, 16)} 
                          onChange={(e) => {
                            const newArr = [...formData.visits_array];
                            newArr[index].visit_time = e.target.value;
                            setFormData({ ...formData, visits_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent' }}
                        />
                      </td>
                      
                      <td style={{ padding: '10px', fontWeight: 'bold' }}>
                        {visit.duty_doctor_name}
                      </td>

                      <td style={{ padding: '10px' }}>
                        <select 
                          value={visit.visit_type} 
                          disabled={isDisabled}
                          onChange={(e) => {
                            const newArr = [...formData.visits_array];
                            newArr[index].visit_type = e.target.value;
                            setFormData({ ...formData, visits_array: newArr });
                          }}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', color: theme.primaryBlue }}
                        >
                          <option value="Morning Round">Morning Round</option>
                          <option value="Evening Round">Evening Round</option>
                          <option value="Emergency visit">Emergency visit</option>
                          <option value="Follow-up">Follow-up</option>
                        </select>
                      </td>
                      
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="text" 
                          value={visit.assessment_notes} 
                          onChange={(e) => {
                            const newArr = [...formData.visits_array];
                            newArr[index].assessment_notes = e.target.value;
                            setFormData({ ...formData, visits_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '100%' }}
                        />
                      </td>
                      
                      <td style={{ padding: '10px' }}>
                         <input 
                          type="text" 
                          value={visit.investigation_advice || ''} 
                          onChange={(e) => {
                            const newArr = [...formData.visits_array];
                            newArr[index].investigation_advice = e.target.value;
                            setFormData({ ...formData, visits_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '100%' }}
                        />
                      </td>

                      <td style={{ padding: '10px' }}>
                         <input 
                          type="text" 
                          value={visit.medication_changes || ''} 
                          onChange={(e) => {
                            const newArr = [...formData.visits_array];
                            newArr[index].medication_changes = e.target.value;
                            setFormData({ ...formData, visits_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '100%' }}
                        />
                      </td>
                      
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {!isDisabled && ( 
                          <button 
                            type="button" 
                            onClick={() => removeVisit(index)} 
                            style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', padding: '5px' }}
                            title="Remove Record"
                          >
                            X
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default IpdDoctorVisits;